import type { AnyMessageEvent, AnyMessageEventHandler, SubscriberForDOMMutation } from "./types.js";

let currentWindow: Window;

// Events and Event handlers
const eventHandlers = new Map<string, AnyMessageEventHandler<any>>();
const events = new Set<string>();

const subscribersForDOMMutations: SubscriberForDOMMutation[] = [];

export const registerEventHandler = <T>(eventName: string, handler: AnyMessageEventHandler<T>) => {
  events.add(eventName);
  eventHandlers.set(eventName, handler);
};

// Iframe sets, observers and handlers
const sameOriginIframeSet = new Set<HTMLIFrameElement>();
const crossOriginIframeSet = new Set<HTMLIFrameElement>();

const loadIframesIntoSet = (iframes: HTMLIFrameElement[], iframeSet: Set<HTMLIFrameElement>) => {
  for (const iframe of iframes) {
    iframeSet.add(iframe);
  }
};

const isSandboxedIframeWithOutAllowSameOrigin = (el: HTMLIFrameElement) => {
  return el.hasAttribute("sandbox") && !el.getAttribute("sandbox")?.includes("allow-same-origin");
};

const isSameOriginIframe = (el: HTMLIFrameElement) => {
  // We are going to consider sandboxed iframes as cross origin iframes because we cannot
  return !isSandboxedIframeWithOutAllowSameOrigin(el) && !!(el.contentDocument && el.contentDocument.body);
};

const removeIFramesFromTheSet = (node: Node) => {
  if (node.nodeName === "IFRAME") {
    if (isSameOriginIframe(node as HTMLIFrameElement)) {
      sameOriginIframeSet.delete(node as HTMLIFrameElement);
    } else {
      crossOriginIframeSet.delete(node as HTMLIFrameElement);
    }
  }
};

const config = { attributes: true, childList: true, subtree: true };

const observeAndQueryForIframes = (el: HTMLElement, observer: MutationObserver, observeEl = false) => {
  const sameOriginIframes: HTMLIFrameElement[] = [];
  const crossOriginIframes: HTMLIFrameElement[] = [];
  if (el) {
    if (observeEl) {
      observer.observe(el, config);
    }
    const iframeEls = [...el.getElementsByTagName("iframe")];
    iframeEls.forEach((iframeEl) => {
      if (isSameOriginIframe(iframeEl)) {
        sameOriginIframes.push(iframeEl);
        const { sameOriginIframes: nestedSameOriginIframes, crossOriginIframes: nestedCrossOriginIframes } =
          observeAndQueryForIframes(iframeEl.contentDocument?.body as HTMLElement, observer, true);
        sameOriginIframes.push(...nestedSameOriginIframes);
        crossOriginIframes.push(...nestedCrossOriginIframes);
      } else if (!isSandboxedIframeWithOutAllowSameOrigin(iframeEl)) {
        // Ignore sandboxed iframes from sameOriginSet as well as crossOriginSet
        crossOriginIframes.push(iframeEl);
      }
    });
  }

  return {
    sameOriginIframes,
    crossOriginIframes,
  };
};

const updateDOMMutationToSubscribers = (
  subscribers: SubscriberForDOMMutation[],
  sameOriginIframes: HTMLIFrameElement[],
  crossOriginIframes: HTMLIFrameElement[]
) => {
  const sameOriginSet = new Set<HTMLIFrameElement>();
  const crossOriginSet = new Set<HTMLIFrameElement>();
  if (sameOriginIframes?.length) {
    sameOriginIframes.forEach((iframe) => sameOriginSet.add(iframe));
  }

  if (crossOriginIframes?.length) {
    crossOriginIframes.forEach((iframe) => crossOriginSet.add(iframe));
  }
  subscribers.forEach((subscriber) => {
    subscriber(sameOriginSet, crossOriginSet);
  });
};

const onDOMMutation = (mutationsList: MutationRecord[]) => {
  for (const mutation of mutationsList) {
    if (mutation.type === "childList" && mutation.addedNodes.length) {
      const { sameOriginIframes, crossOriginIframes } = observeAndQueryForIframes(
        mutation.target as HTMLElement,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        mutationObserver
      );
      updateDOMMutationToSubscribers(subscribersForDOMMutations, sameOriginIframes, crossOriginIframes);
      loadIframesIntoSet(sameOriginIframes, sameOriginIframeSet);
      loadIframesIntoSet(crossOriginIframes, crossOriginIframeSet);
    }

    if (mutation.type === "childList") {
      for (let i = 0; i < mutation.removedNodes.length; i++) {
        removeIFramesFromTheSet(mutation.removedNodes[i]);
      }
    }
  }
};

const mutationObserver = new MutationObserver(onDOMMutation);

export const subscribeForDOMMutations = (callback: SubscriberForDOMMutation) => {
  subscribersForDOMMutations.push(callback);
};

const isAmazonSubdomain = (hostOrigin: string): boolean => {
  return /\.amazon\.com$/.test(hostOrigin) || /\.a2z\.com$/.test(hostOrigin);
};

// Window and message handlers
export const invokeHandlers = (
  window: Window,
  sameOriginIframes: Set<HTMLIFrameElement>,
  crossOriginIframes: Set<HTMLIFrameElement>
) => {
  return (event: AnyMessageEvent) => {
    if (event && event.data && events.has(event.data.type)) {
      if (!isAmazonSubdomain(event.origin)) {
        console.warn("Message event origin should be from one of the amazon sub domains");
        return;
      }
      const handler = eventHandlers.get(event.data.type);
      if (handler) {
        handler({
          event,
          sameOriginIframeSet: sameOriginIframes,
          crossOriginIframeSet: crossOriginIframes,
          window,
        });
      }
    }
  };
};

export const getSameOriginIframeSet = () => sameOriginIframeSet;

export const getCrossOriginIframeSet = () => crossOriginIframeSet;

export const getCurrentWindow = () => currentWindow;

const onPostMessage = (window: Window, listener: EventListener) => {
  window.addEventListener("message", listener);
};

export const startWindowHandler = (window: Window) => {
  const eventListener = invokeHandlers(window, sameOriginIframeSet, crossOriginIframeSet);
  onPostMessage(window, eventListener as EventListener);
  currentWindow = window;
};

export const startConsoleIframeHandler = (window: Window) => {
  startWindowHandler(window);

  const { sameOriginIframes, crossOriginIframes } = observeAndQueryForIframes(
    window.document.body,
    mutationObserver,
    true
  );
  loadIframesIntoSet(sameOriginIframes, sameOriginIframeSet);
  loadIframesIntoSet(crossOriginIframes, crossOriginIframeSet);
};
